if (!customElements.get('card-product-slider')) {
  class CardProductSlider extends HTMLElement {
    constructor() {
      super();

      const swiperOptions =
        JSON.parse(this.getAttribute('data-swiper-options')) || {};

      this.sliderWrapper = this.querySelector('.swiper-wrapper');
      this.requiresFetch = this.hasAttribute('data-url');
      this.parent = this.closest('[data-section-id]');
      this._faResizeHandler = () => this.syncHeights();
      if (this.hasAttribute('data-fa-pdp-related')) {
        this.parent = this.closest('.product-feed') || this.parent;
      }
      this.sliderOptions = {
        a11y: false,
        threshold: 3,
        spaceBetween: swiperOptions.spaceBetweenMobile || 2,
        slidesPerView: swiperOptions.slidesPerViewMobile || 'auto',
        resistanceRatio: 0.72,
        breakpoints: {
          750: {
            slidesPerView: 3,
            spaceBetween: swiperOptions.spaceBetweenDesktop || 2
          },
          1100: {
            slidesPerView: 4,
            spaceBetween: swiperOptions.spaceBetweenDesktop || 2
          }
        }
      };
      if (this.hasAttribute('data-fa-pdp-related')) {
        this.sliderOptions.slidesPerView = 2.5;
      }
      if (swiperOptions.navigation) {
        this.sliderOptions.navigation = {
          ...swiperOptions.navigation
        };
      }
      if (swiperOptions.autoplay) {
        this.sliderOptions.autoplay = {
          ...swiperOptions.autoplay
        };
      }
      if (swiperOptions.loop) {
        this.sliderOptions.loop = swiperOptions.loop;
      }
    }

    connectedCallback() {
      if (this.requiresFetch) {
        this.fetchItems();
        return;
      }
      this.initSlider();
      this.syncHeights();
      window.addEventListener("resize", this._faResizeHandler, { passive: true });
    }

    disconnectedCallback() {
      window.removeEventListener("resize", this._faResizeHandler);
      this._faFetchAbort?.abort();
      this._faFetchAbort = null;
      if (this.slider && !this.slider.destroyed) {
        this.slider.destroy(true, true);
      }
      this.slider = null;
    }

    syncHeights() {
      const textSlide = this.querySelector('.products-feed__card-text-custom-slide');
      const actionElement = this.querySelector('.product-card__actions');

      if (textSlide && actionElement) {
        const actionHeight = actionElement.offsetHeight;
        textSlide.style.height = `${actionHeight}px`;
      }
    }

    fetchItems() {
      if (!this.parent) throw new Error('Parent is missing');
      this._faFetchAbort?.abort();
      const controller = new AbortController();
      this._faFetchAbort = controller;
      fetch(this.getAttribute('data-url'), { signal: controller.signal })
        .then(response => response.text())
        .then(text => {
          if (!this.isConnected) return;
          const html = new DOMParser().parseFromString(
            text,
            'text/html'
          );
          const responseFeed = html.querySelector('.section--product-recommendations card-product-slider')
            || html.querySelector('card-product-slider[data-fa-pdp-related]');
          const wrapper = responseFeed?.querySelector('.swiper-wrapper') || html.querySelector('.swiper-wrapper');

          if (this.hasAttribute('data-fa-pdp-related')) {
            const selected = this.selectPdpRecommendations(responseFeed, wrapper);
            if (selected.length) {
              const target = this.querySelector('.swiper-wrapper');
              if (!target) return;
              target.replaceChildren(...selected);
              this.parent.classList.remove('hidden');
              this.initSlider();
            } else {
              /* No meaningful candidates: don't leave a blank recommendations section. */
              this.parent.closest('.gradient')?.remove();
            }
          } else if (wrapper && wrapper.innerHTML.trim().length) {
            this.querySelector('.swiper-wrapper').innerHTML =
              wrapper.innerHTML;
            this.parent.classList.remove('hidden');
            this.initSlider();
          }
        })
        .catch(e => {
          if (e.name !== 'AbortError') console.error(e);
        });
    }

    /*
      Far Afield PDP recommendations.
      Keep cart "Why not add" untouched. Ranking uses the same useful ideas:
      meaningful shared collection, complementary family and size availability,
      plus garment-specific material/pattern cues. Category alone isn't enough.
      Only the chosen cards ever enter the active Swiper / download images.
    */
    selectPdpRecommendations(responseFeed, relatedWrapper) {
      if (!responseFeed) return [];

      const curated = relatedWrapper
        ? Array.from(relatedWrapper.querySelectorAll('[data-fa-recommendation-candidate]'))
        : [];
      const fallbackTemplate = responseFeed.querySelector('template[data-fa-pdp-candidates]');
      const fallback = fallbackTemplate
        ? Array.from(fallbackTemplate.content.querySelectorAll('[data-fa-recommendation-candidate]'))
        : [];

      const sourceId = this.dataset.sourceId;
      const sourceFamily = this.dataset.sourceFamily || 'unknown';
      const sourceCollections = this.faPipeSet(this.dataset.sourceCollections);
      const sourceTokens = this.faFeatureTokens(
        `${this.dataset.sourceTitle || ''} ${this.dataset.sourceTags || ''}`
      );
      const sourceSale = this.dataset.sourceOnSale === 'true';

      /* Exclude real linked colourways and actual outfit items from Worn With. */
      const omittedHandles = new Set();
      const existingLinks = document.querySelectorAll(
        '.main-product .product__product-group a[href], ' +
        '.main-product .fa-pdp-groups-slot a[href], ' +
        '.main-product .product__complementaries--worn-with a[href]'
      );
      existingLinks.forEach(link => {
        const match = link.getAttribute('href')?.match(/\/products\/([^/?#]+)/);
        if (match) omittedHandles.add(match[1]);
      });

      const genericCollections = new Set([
        'all', 'all-products', 'new-arrivals', 'new-arrivals-us',
        'shirts', 'trousers', 'shorts', 'knitwear', 'outerwear',
        'overshirts', 't-shirts', 'sweatshirts', 'jersey',
        'accessories', 'hats', 'socks', 'scarves', 'sale'
      ]);
      const preferences = {
        shirts: ['knitwear', 'trousers', 'outerwear', 'jersey'],
        knitwear: ['trousers', 'shirts', 'outerwear', 'jersey'],
        trousers: ['shirts', 'knitwear', 'outerwear', 'jersey'],
        outerwear: ['knitwear', 'shirts', 'trousers', 'jersey'],
        shorts: ['shirts', 'jersey', 'knitwear', 'accessories'],
        jersey: ['trousers', 'outerwear', 'knitwear', 'shirts'],
        accessories: ['outerwear', 'knitwear', 'shirts', 'trousers'],
        unknown: ['shirts', 'knitwear', 'trousers', 'outerwear']
      };
      const matchingFamily = preferences[sourceFamily] || preferences.unknown;
      const byId = new Map();
      let order = 0;

      for (const node of [...curated, ...fallback]) {
        const d = node.dataset;
        const id = d.productId;
        if (!id || id === sourceId || byId.has(id) || d.available !== 'true') continue;
        if (omittedHandles.has(d.handle)) continue;

        const total = Number(d.totalSizes || 0);
        const stocked = Number(d.stockedSizes || 0);
        /* A garment with one surviving size out of several isn't a useful suggestion. */
        if (total >= 3 && stocked < 2) continue;
        if (total > 0 && stocked === 0) continue;

        const candidateCollections = this.faPipeSet(d.collections);
        let commonCollectionCount = 0;
        for (const handle of candidateCollections) {
          if (sourceCollections.has(handle) && !genericCollections.has(handle)) {
            commonCollectionCount++;
          }
        }
        const tokens = this.faFeatureTokens(`${d.productTitle || ''} ${d.tags || ''} ${d.productType || ''}`);
        let commonFeatures = 0;
        for (const token of tokens) {
          if (sourceTokens.has(token)) commonFeatures++;
        }

        const family = d.family || 'unknown';
        let score = d.source === 'shopify' ? 42 : 0;
        if (family === sourceFamily && family !== 'unknown') score += 15;
        const preferenceIndex = matchingFamily.indexOf(family);
        if (family !== sourceFamily && preferenceIndex >= 0) {
          score += 24 - 4 * preferenceIndex;
        }
        score += Math.min(2, commonCollectionCount) * 27;
        score += Math.min(3, commonFeatures) * 16;
        score += total ? Math.round((stocked / total) * 16) : 12;
        if (total >= 3 && stocked === 2) score -= 5;
        if (!sourceSale && d.onSale === 'true') score -= 35;
        if (!sourceSale && d.finalSale === 'true') score -= 40;
        /* Manual Shopify recommendations retain influence, but not a free pass. */
        if (d.source === 'shopify') score -= Math.min(order, 10) * 0.5;
        if (score < 33) continue;
        byId.set(id, { node, family, score, order });
        order++;
      }

      const sorted = Array.from(byId.values())
        .sort((a, b) => b.score - a.score || a.order - b.order);
      const limit = Math.min(8, Math.max(1, Number(this.dataset.faLimit || 8)));
      const selected = [];
      const selectedIds = new Set();
      const selectedStyles = new Set();
      const familyCounts = new Map();
      const add = entry => {
        const styleKey = this.faStyleKey(entry.node.dataset.productTitle);
        if (selectedIds.has(entry.node.dataset.productId) || selected.length >= limit) return;
        if (styleKey && selectedStyles.has(styleKey)) return;
        selected.push(entry.node);
        selectedIds.add(entry.node.dataset.productId);
        if (styleKey) selectedStyles.add(styleKey);
        familyCounts.set(entry.family, (familyCounts.get(entry.family) || 0) + 1);
      };

      /* First pass: cross-category, without letting one family dominate. */
      for (const entry of sorted) {
        if (selected.length >= limit) break;
        if ((familyCounts.get(entry.family) || 0) >= 2) continue;
        add(entry);
      }
      /* Second pass: allow further strong matches rather than empty slots. */
      for (const entry of sorted) {
        if (selected.length >= limit) break;
        if ((familyCounts.get(entry.family) || 0) >= 3) continue;
        add(entry);
      }
      /* Third pass: if the catalogue genuinely offers fewer families, show
         the worthwhile options rather than forcing weaker recommendations. */
      for (const entry of sorted) {
        if (selected.length >= limit) break;
        add(entry);
      }
      return selected;
    }

    faStyleKey(title = '') {
      /* Treat obvious colour-only alternatives as one suggestion. */
      const colours = new Set([
        'navy', 'black', 'white', 'olive', 'stone', 'brown', 'cream',
        'beige', 'tan', 'khaki', 'blue', 'green', 'grey', 'gray',
        'rust', 'red', 'orange', 'tobacco', 'indigo', 'natural',
        'ecru', 'camel', 'charcoal', 'sand', 'aqua', 'pink', 'yellow'
      ]);
      const words = String(title).toLowerCase().split(/[^a-z0-9]+/).filter(Boolean);
      while (words.length > 2 && colours.has(words[words.length - 1])) words.pop();
      return words.length > 1 ? words.join('-') : '';
    }

    faPipeSet(value = '') {
      return new Set(String(value).split('|').map(s => s.trim().toLowerCase()).filter(Boolean));
    }

    faFeatureTokens(text = '') {
      /* Specific signals only. Exclude generic words such as 'shirt',
         'new', 'blue' or ordinary marketing tags from the similarity score. */
      const dictionary = new Set([
        'linen', 'wool', 'cotton', 'hemp', 'tencel', 'viscose',
        'corduroy', 'canvas', 'denim', 'seersucker', 'crochet',
        'jacquard', 'dobby', 'twill', 'ripstop', 'flannel',
        'basket', 'weave', 'ribbed', 'waffle', 'slub',
        'stripe', 'striped', 'check', 'checked', 'gingham',
        'embroidered', 'embroidery', 'printed', 'floral',
        'pleated', 'workwear', 'oversized', 'relaxed',
        'cropped', 'tapered', 'boxy', 'wide', 'brushed',
        'organic', 'quilted', 'merino', 'mohair'
      ]);
      const result = new Set();
      for (const term of String(text).toLowerCase().split(/[^a-z0-9]+/)) {
        const normalized = ({ striped: 'stripe', checked: 'check', embroidery: 'embroidered' })[term] || term;
        if (dictionary.has(normalized)) result.add(normalized);
      }
      return result;
    }

    initSlider(slider = this) {
      this.slider = new Swiper(slider, this.sliderOptions);

      this.slider.wrapperEl.setAttribute('aria-live', 'polite');
      this.slider.wrapperEl.id = `swiper-wrapper-${this.dataset.sectionId}`;
      this.slider.slides.forEach((slide, i) => {
        slide.setAttribute('role', 'group');
        slide.setAttribute(
          'aria-label',
          `${i + 1} / ${this.slider.slides.length}`
        );
        slide.dataset.index = i;
      });

      if (this.swiperOptions?.navigation) {
        const sliderButtons = [
          this.slider.navigation.prevEl,
          this.slider.navigation.nextEl
        ];

        sliderButtons.forEach((button, i) => {
          let type = 'Previous';

          if (i === 1) {
            type = 'Next';
          }

          button.setAttribute('aria-label', `${type} slide`);
          button.setAttribute(
            'aria-controls',
            this.slider.wrapperEl.id
          );
        });
      }

      this.slider.wrapperEl.addEventListener('focusin', e => {
        this.slider.el.scrollLeft = 0;

        const slide = e.target.closest('.swiper-slide');
        const slideIndex = +slide.dataset.index;
        const isFirstSlide = slideIndex === 0;
        const isLastSlide =
          slideIndex === this.slider.slides.length - 1;

        if (isFirstSlide || isLastSlide) {
          this.slider.slideTo(slideIndex, 0);

          return;
        }

        const slideRect = slide.getBoundingClientRect();
        const wrapperWidth = this.slider.wrapperEl.clientWidth;
        // prettier-ignore
        const slideLeft = slideRect.x + slideRect.width - ((document.body.clientWidth - wrapperWidth) / 2);
        const isSlideVisible =
          slideLeft <= wrapperWidth && slideLeft >= slideRect.width;

        if (isSlideVisible) {
          return;
        }

        slideLeft >= slideRect.width
          ? this.slider.slideNext(0)
          : this.slider.slidePrev(0);
      });

      const swiperOptions = JSON.parse(this.getAttribute('data-swiper-options')) || {};
      /** stop / start autoplay on hover if quick cart is not open */
      this.slider.wrapperEl.addEventListener('mouseenter', () =>
        this.slider.autoplay.stop()
      );
      this.slider.wrapperEl.addEventListener('mouseleave', () => {
        if (!this.classList.contains('product--open-on-quick-cart') && this.sliderOptions.autoplay) {
          this.slider.autoplay.start();
        }
        if (swiperOptions.autoplay === false) {
          this.slider.autoplay.stop();
        }
      });

      if (
        this.hasAttribute('data-init-quick-cart') &&
        document.querySelector('quick-cart-drawer') &&
        this.querySelector('.swiper-slide')
      ) {
        document.querySelector('quick-cart-drawer').init();
      }

      this.querySelectorAll('product-card').forEach(card => {
        card.init();
      });

      if (Shopify.designMode) {
        if (
          this.hasAttribute('data-init-quick-cart') &&
          document.querySelector('quick-cart-drawer') &&
          this.querySelector('.swiper-slide')
        ) {
          document.querySelector('quick-cart-drawer').init();
        }
      }
    }
  }

  customElements.define('card-product-slider', CardProductSlider);
}
